export const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '543532199868267';
export const META_CURRENCY = 'UYU';

const CHECKOUT_STORAGE_KEY = 'remurban_meta_checkout';
const PURCHASE_STORAGE_PREFIX = 'remurban_meta_purchase_';
const trackedPurchases = new Set();

const asPositiveNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
};

const roundMoney = (value) => Math.round(asPositiveNumber(value) * 100) / 100;

/**
 * Meta debe recibir el mismo ID que identifica la ficha del producto y el
 * artículo del catálogo. En Rem Urban ese ID es el _id público de MongoDB.
 * Los SKU identifican variantes y no se mezclan con el ID del artículo padre.
 */
export const getMetaCatalogId = (product) => {
  const id = product?._id ?? product?.id;
  return id == null ? '' : String(id).trim();
};

export const getMetaItemPrice = (item) => {
  const rawPrice = item?.price ?? item?.selectedVariant?.price ?? item?.basePrice;
  const discount = asPositiveNumber(item?.discount);
  const basePrice = asPositiveNumber(rawPrice);

  if (item?.price == null && discount > 0) {
    return roundMoney(basePrice * (1 - discount / 100));
  }

  return roundMoney(basePrice);
};

export const buildMetaContents = (items = []) => items
  .map((item) => {
    const id = getMetaCatalogId(item);
    if (!id) return null;

    return {
      id,
      quantity: Math.max(1, Math.floor(asPositiveNumber(item?.quantity, 1))),
      item_price: getMetaItemPrice(item)
    };
  })
  .filter(Boolean);

export const buildMetaCommerceParams = (items = [], value) => {
  const contents = buildMetaContents(items);
  const calculatedValue = contents.reduce(
    (total, item) => total + (item.item_price * item.quantity),
    0
  );

  return {
    content_ids: [...new Set(contents.map((item) => item.id))],
    content_type: 'product',
    contents,
    currency: META_CURRENCY,
    value: roundMoney(value == null ? calculatedValue : value),
    num_items: contents.reduce((total, item) => total + item.quantity, 0)
  };
};

export const initializeMetaPixel = () => {
  if (typeof window === 'undefined' || !META_PIXEL_ID) return false;

  if (!window.fbq) {
    const fbq = function (...args) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue.push(args);
      }
    };

    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.id = 'meta-pixel-script';
    document.head.appendChild(script);
  }

  window.__remUrbanMetaPixels = window.__remUrbanMetaPixels || {};
  if (!window.__remUrbanMetaPixels[META_PIXEL_ID]) {
    window.fbq('init', META_PIXEL_ID);
    window.__remUrbanMetaPixels[META_PIXEL_ID] = true;
  }

  return true;
};

export const trackMetaEvent = (eventName, params = {}, eventId) => {
  if (!eventName || !initializeMetaPixel()) return false;

  if (eventId) {
    window.fbq('track', eventName, params, { eventID: String(eventId) });
  } else {
    window.fbq('track', eventName, params);
  }

  return true;
};

export const trackPageView = () => trackMetaEvent('PageView');

export const trackViewContent = (product) => {
  const id = getMetaCatalogId(product);
  if (!id) return false;

  const basePrice = asPositiveNumber(product?.basePrice ?? product?.price);
  const discount = asPositiveNumber(product?.discount);
  const effectivePrice = discount > 0
    ? roundMoney(basePrice * (1 - discount / 100))
    : roundMoney(basePrice);

  return trackMetaEvent('ViewContent', {
    ...buildMetaCommerceParams([{ ...product, price: effectivePrice, quantity: 1 }]),
    content_name: product?.name || '',
    content_category: product?.category?.name || product?.category || ''
  });
};

export const trackAddToCart = (item) => {
  const id = getMetaCatalogId(item);
  if (!id) return false;

  return trackMetaEvent('AddToCart', {
    ...buildMetaCommerceParams([item]),
    content_name: item?.name || '',
    content_category: item?.category?.name || item?.category || ''
  });
};

export const trackInitiateCheckout = (items, value) => {
  const params = buildMetaCommerceParams(items, value);
  if (params.contents.length === 0) return false;
  return trackMetaEvent('InitiateCheckout', params);
};

export const trackPurchaseOnce = ({ transactionId, items, value, paymentMethod }) => {
  const normalizedTransactionId = transactionId == null ? '' : String(transactionId).trim();
  const params = buildMetaCommerceParams(items, value);

  if (!normalizedTransactionId || params.contents.length === 0) return false;

  const storageKey = `${PURCHASE_STORAGE_PREFIX}${normalizedTransactionId}`;
  if (trackedPurchases.has(storageKey)) return false;

  try {
    if (window.localStorage?.getItem(storageKey)) {
      trackedPurchases.add(storageKey);
      return false;
    }
  } catch (_) {
    // Algunos navegadores bloquean storage; el Set mantiene deduplicación en memoria.
  }

  const tracked = trackMetaEvent('Purchase', {
    ...params,
    order_id: normalizedTransactionId,
    ...(paymentMethod ? { payment_method: paymentMethod } : {})
  }, `purchase-${normalizedTransactionId}`);

  if (!tracked) return false;

  trackedPurchases.add(storageKey);
  try {
    window.localStorage?.setItem(storageKey, new Date().toISOString());
  } catch (_) {
    // El evento ya fue encolado aunque no sea posible persistir la marca.
  }

  return true;
};

export const saveCheckoutSnapshot = (snapshot) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (_) {
    // La compra puede continuar aunque el navegador bloquee sessionStorage.
  }
};

export const readCheckoutSnapshot = () => {
  if (typeof window === 'undefined') return null;
  try {
    const snapshot = window.sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    return snapshot ? JSON.parse(snapshot) : null;
  } catch (_) {
    return null;
  }
};

export const clearCheckoutSnapshot = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
  } catch (_) {
    // No hay nada más que limpiar si storage está bloqueado.
  }
};
